import { Mic, MicOff, Loader2 } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface VoiceSearchButtonProps {
  onTranscript: (transcript: string) => void;
  className?: string;
  size?: 'sm' | 'default' | 'lg';
  isActive?: boolean;
  onActiveChange?: (active: boolean) => void;
}

export function VoiceSearchButton({
  onTranscript,
  className,
  size = 'default',
  isActive = false,
  onActiveChange,
}: VoiceSearchButtonProps) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    // Check if Web Speech API is supported
    if (
      typeof window !== 'undefined' &&
      ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
    ) {
      setIsSupported(true);
    }
  }, []);

  // Handle external activation
  useEffect(() => {
    if (isActive && !isListening && isSupported) {
      startListening();
    }
  }, [isActive]);

  const startListening = () => {
    if (!isSupported) {
      setError('Voice search is not supported in your browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      if (onActiveChange) onActiveChange(true);
    };

    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0])
        .map((result) => result.transcript)
        .join('');

      if (event.results[0].isFinal) {
        onTranscript(transcript);
      }
    };

    recognition.onerror = (event: any) => {
      setError(event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (onActiveChange) onActiveChange(false);
    };

    recognition.start();
    recognitionRef.current = recognition;
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <div className="relative">
      <Button
        type="button"
        variant={isListening ? 'destructive' : 'outline'}
        size={size}
        onClick={toggleListening}
        className={cn(
          'relative transition-[colors,opacity,transform]',
          isListening && 'animate-pulse',
          className
        )}
        title={isListening ? 'Stop listening' : 'Start voice search'}
      >
        {isListening ? (
          <>
            <MicOff
              className={cn('h-4 w-4', size === 'sm' && 'h-3 w-3', size === 'lg' && 'h-5 w-5')}
            />
            {size !== 'sm' && <span className="ml-2">Listening...</span>}
          </>
        ) : (
          <Mic className={cn('h-4 w-4', size === 'sm' && 'h-3 w-3', size === 'lg' && 'h-5 w-5')} />
        )}
      </Button>

      {error && (
        <div className="absolute top-full mt-1 text-xs text-red-600 whitespace-nowrap">{error}</div>
      )}
    </div>
  );
}

// TypeScript declarations for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
